import React, { useEffect, useState } from 'react';
import { Col, Form, Modal } from 'antd';
import { toast } from 'react-toastify';
import Section from 'components/Section';
import getConfig from 'next/config';
import QuizCategory from './Category/QuizCategory';
import { PrivacyDisclaimerText, ShowPrivacyDisclaimerButton } from 'components/DynamicForm';
import Field from 'components/DynamicForm/Field';
import Button from 'components/Button';
import SubmitError from './Notifications/SubmitError';
import { useRouter } from 'next/router';
import SubmitSuccess from './Notifications/SubmitSuccess';

export type Votes = {
    [categoryId: number]: string[];
};

export interface FormValues {
    email?: string;
    votes: {
        [questionId: number]: string | string[];
    };
}

type Props = {
    contest: QuizContest;
};

export const Quiz = ({ contest }: Props) => {
    const [isSent, setSent] = useState(false);
    const [isProcessing, setProcessing] = useState(false);
    const [showHints, setShowHints] = useState(false);
    const router = useRouter();
    useEffect(function () {
        if (
            contest.form &&
            !contest.form.allowMultipleSubmits &&
            window.localStorage.getItem(`form_${contest.form.uuid}`)
        ) {
            setSent(true);
        }
    }, []);

    const showPrivacyModal = () => {
        Modal.info({
            okText: 'Zamknij',
            width: '80%',
            title: 'Informacja o przetwarzaniu danych osobowych',
            content: <div dangerouslySetInnerHTML={{ __html: contest.form.privacyDisclaimer }} />
        });
    };

    const [form] = Form.useForm();

    const showSummaryModal = (points: number) => {
        const summary = contest.contestSummaries.find(({ maxResult, minResult }) => {
            return minResult <= points && maxResult > points;
        });

        const displayModal = (
            type: 'info' | 'success' | 'error' | 'warning',
            content: string | React.ReactNode
        ) => {
            Modal[type]({
                okText: 'Zamknij',
                onOk: () => {
                    handleClearForm();
                },
                width: '80%',
                title: 'Dziękujemy udział w konkursie!',
                content
            });
        };
        if (!summary) {
            displayModal(
                'info',
                <div>
                    <p>Dziękujemy udział w konkursie!</p>
                    <p>Twój wynik to: {points}</p>
                </div>
            );
            return;
        }

        displayModal(
            summary.type === 'default' ? 'info' : summary.type,
            <div dangerouslySetInnerHTML={{ __html: summary.description }} />
        );
    };

    const calculatePoints = (data: FormValues) => {
        let points = 0;
        Object.entries(data.votes).forEach(([id, answerSlug]) => {
            const questionId = parseInt(id);
            const question = contest.categories.find((category) => category.id === questionId);
            if (!question) return;
            if (question && typeof answerSlug === 'string') {
                if (question.type === 'textarea') return;
                const answer = question.questions.find((question) => question.slug === answerSlug);
                points += answer.rating;
            }

            if (question && Array.isArray(answerSlug)) {
                question.questions.forEach((answer) => {
                    if (answerSlug.includes(answer.slug)) {
                        points += answer.rating;
                    }
                });
            }
        });

        return points;
    };

    const handleClearForm = () => {
        form.resetFields();
        setSent(false);
        setProcessing(false);
        setShowHints(false);
        router.reload();
    };

    const handleSubmit = (data: FormValues) => {
        const { apiUrl } = getConfig().publicRuntimeConfig;
        setProcessing(true);
        const points = calculatePoints(data);
        setShowHints(true);
        if (contest.contestSummaries !== null && contest.contestSummaries.length > 0) {
            showSummaryModal(points);
        } else {
            if (!data.email) {
                handleClearForm();
                return;
            }
        }

        if (!data.email) {
            return;
        }

        const { votes, ...formData } = data;
        const { seoSlug } = contest;
        fetch(`${apiUrl}/public/api/contests/${seoSlug}/votes`, {
            method: 'POST',
            headers: {
                Accept: 'application/json, text/plain, */*',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                contestData: { votes, points },
                email: data.email,
                form: formData
            })
        })
            .then((response) => {
                if (!response.ok) throw response;

                return response.json();
            })
            .then(() => {
                toast.success(<SubmitSuccess />);
            })
            .catch(async (response) => {
                const data = await response.json();
                const parsedData = JSON.parse(data);

                if (parsedData?.error === 'already_submitted') {
                    toast.error(<SubmitError type="already-submitted" />);
                    return;
                }
                if (parsedData?.error === 'expired') {
                    toast.error(<SubmitError type="expired" />);
                    return;
                }
                toast.error(<SubmitError type="default" />);
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    return (
        <Form
            form={form}
            name="vote"
            onFinish={handleSubmit}
            validateMessages={{
                required: 'To pole jest wymagane!'
            }}
            scrollToFirstError
        >
            {contest.categories.map((category) => (
                <QuizCategory
                    key={`category__${category.id}`}
                    category={category}
                    showHints={showHints}
                />
            ))}
            {contest.form && (
                <>
                    <Section justify="center">
                        {contest.form.config.map((field) => (
                            <Col span={24} key={`form-${contest.form.name}-field-${field.label}`}>
                                <Field config={field} formState={{ isSent, isProcessing }} />
                            </Col>
                        ))}
                    </Section>
                    <Section justify="center">
                        <Col span={24}>
                            {contest.form.showPrivacyDisclaimer && (
                                <PrivacyDisclaimerText>
                                    Wypełniając formularz, akceptujesz{' '}
                                    <ShowPrivacyDisclaimerButton
                                        type="button"
                                        onClick={showPrivacyModal}
                                    >
                                        regulamin.
                                    </ShowPrivacyDisclaimerButton>
                                </PrivacyDisclaimerText>
                            )}
                        </Col>
                    </Section>
                </>
            )}
            {!contest.form && (
                <Section justify="center">
                    <Col span={24}>
                        <Button type="submit">Pokaż wynik</Button>
                    </Col>
                </Section>
            )}
            {isSent && (
                <Section justify="center">
                    <Col span={24}>
                        <Button onClick={handleClearForm} type="button">
                            Wyczyść
                        </Button>
                    </Col>
                </Section>
            )}
        </Form>
    );
};

export default Quiz;
